import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest, apiConfig } from "../config/authConfig";
import {
  isDevAuthEnabled,
  isDevAuthenticated,
  getDevToken,
  getDevUser,
  clearDevAuth,
} from "../services/devAuth";
import {
  setToken,
  clearToken,
  setTokenRefreshCallback,
} from "../services/tokenStore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialAuthDone = useRef(false);

  // Check if authenticated (MSAL or Dev mode)
  const isAuthenticated =
    isMsalAuthenticated || (isDevAuthEnabled() && isDevAuthenticated());

  // Fetch user profile from backend
  const fetchUserProfile = async (accessToken) => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        return data;
      } else if (response.status === 403) {
        setError(
          "Utente non registrato nel portale. Contattare l'amministratore.",
        );
      } else if (response.status === 401) {
        setError("Sessione scaduta. Effettuare nuovamente il login.");
        clearDevAuth();
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
      setError("Errore nel recupero del profilo utente");
    }
    return null;
  };

  // Get ID token for backend authentication (MSAL or Dev mode)
  // NOTA: Con Azure CIAM, usiamo l'idToken (non accessToken) per autenticarci
  // con il nostro backend. L'accessToken è per Microsoft Graph API.
  const getAccessToken = useCallback(async () => {
    // Dev mode: use stored JWT
    if (isDevAuthEnabled() && isDevAuthenticated()) {
      const devToken = getDevToken();
      setToken(devToken); // Aggiorna tokenStore
      return devToken;
    }

    // MSAL mode
    if (!accounts[0]) {
      clearToken();
      return null;
    }

    try {
      // Check if the cached ID token is expired or close to expiry (5 min buffer).
      // MSAL's acquireTokenSilent only auto-refreshes access tokens, not ID tokens.
      // Since we use the ID token for backend auth, we must force refresh when expired.
      let forceRefresh = false;
      const cachedIdToken = accounts[0]?.idTokenClaims;
      if (cachedIdToken?.exp) {
        const timeUntilExpiry =
          cachedIdToken.exp - Math.floor(Date.now() / 1000);
        if (timeUntilExpiry < 300) {
          // Less than 5 minutes to expiry
          console.log(
            "[Auth] ID token expires in",
            timeUntilExpiry,
            "seconds, forcing refresh",
          );
          forceRefresh = true;
        }
      }

      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
        forceRefresh,
      });
      setToken(response.idToken);
      return response.idToken;
    } catch (err) {
      try {
        const response = await instance.acquireTokenPopup(loginRequest);
        setToken(response.idToken); // Aggiorna tokenStore
        return response.idToken;
      } catch (popupErr) {
        console.error("Token acquisition failed:", popupErr);
        clearToken();
        return null;
      }
    }
  }, [instance, accounts]);

  // Login (MSAL redirect)
  const login = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error("Login failed:", err);
      setError("Login fallito");
    }
  };

  // Logout
  const logout = async () => {
    console.log("=== LOGOUT START ===");

    // Pulisci tokenStore in ogni caso
    clearToken();

    // Dev mode logout
    if (isDevAuthEnabled()) {
      clearDevAuth();
      setUser(null);
      window.location.href = "/login";
      return;
    }

    console.log("[LOGOUT] MSAL mode - starting logout");

    // Salva l'ID token PRIMA di pulire lo storage (serve per id_token_hint)
    const currentAccount = accounts?.[0];
    let idTokenHint = null;
    if (currentAccount) {
      // Cerca l'ID token nella cache MSAL
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes("idtoken")) {
          try {
            const val = JSON.parse(localStorage.getItem(key));
            if (val?.secret) {
              idTokenHint = val.secret;
              break;
            }
          } catch {
            // skip
          }
        }
      }
    }

    // Pulisci stato locale
    setUser(null);

    // Pulisci tutto lo storage
    localStorage.clear();
    sessionStorage.clear();

    // Redirect a Azure CIAM logout endpoint con id_token_hint per auto-redirect
    const tenantId = "f98ceced-ed97-45bc-a978-a335f65d2381";
    const tenantName = "phcmanutenzioniauth";
    // Usa l'origin esatto registrato come SPA redirect URI in CIAM
    const postLogoutUri = encodeURIComponent(window.location.origin);
    let azureLogoutUrl = `https://${tenantName}.ciamlogin.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutUri}`;
    if (idTokenHint) {
      azureLogoutUrl += `&id_token_hint=${encodeURIComponent(idTokenHint)}`;
    }

    console.log("[LOGOUT] Redirecting to Azure logout");
    window.location.href = azureLogoutUrl;
  };

  // Fallback logout when no id_token_hint is available
  const fallbackLogout = () => {
    localStorage.clear();
    sessionStorage.clear();

    const tenantId = "f98ceced-ed97-45bc-a978-a335f65d2381";
    const tenantName = "phcmanutenzioniauth";
    const postLogoutUri = encodeURIComponent(window.location.origin);
    const azureLogoutUrl = `https://${tenantName}.ciamlogin.com/${tenantId}/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutUri}`;

    window.location.href = azureLogoutUrl;
  };

  // Registra il callback per il refresh del token
  useEffect(() => {
    setTokenRefreshCallback(getAccessToken);
  }, [getAccessToken]);

  // Effect to handle authentication state changes
  useEffect(() => {
    const handleAuth = async () => {
      console.log("[AuthContext] handleAuth called");
      console.log("[AuthContext] isMsalAuthenticated:", isMsalAuthenticated);
      console.log("[AuthContext] inProgress:", inProgress);
      console.log(
        "[AuthContext] accounts:",
        accounts?.length,
        accounts?.[0]?.username,
      );

      // Wait for MSAL to finish initialization/interaction before making auth decisions
      if (inProgress !== InteractionStatus.None) {
        console.log("[AuthContext] MSAL interaction in progress, waiting...");
        return; // Keep loading=true, don't make premature auth decisions
      }

      setError(null);

      // Check dev mode first
      if (isDevAuthEnabled() && isDevAuthenticated()) {
        console.log("[AuthContext] Dev mode detected");
        const devUser = getDevUser();
        if (devUser) {
          setUser({
            email: devUser.email,
            displayName: devUser.name,
            ruoloLabel: devUser.role,
            isTestUser: true,
          });

          const token = await getAccessToken();
          if (token) {
            await fetchUserProfile(token);
          }
        }
        initialAuthDone.current = true;
        setLoading(false);
        return;
      }

      // MSAL authentication
      if (isMsalAuthenticated && accounts[0]) {
        console.log("[AuthContext] MSAL authenticated, fetching profile...");
        const token = await getAccessToken();
        if (token) {
          console.log("[AuthContext] Got token, fetching user profile");
          await fetchUserProfile(token);
        } else {
          console.log("[AuthContext] No token available");
        }
      } else {
        console.log("[AuthContext] Not authenticated, clearing user");
        setUser(null);
        clearToken();
      }

      initialAuthDone.current = true;
      setLoading(false);
    };

    handleAuth();
  }, [isMsalAuthenticated, accounts, inProgress, getAccessToken]);

  // API request helper with auth
  const apiRequest = async (endpoint, options = {}) => {
    const token = await getAccessToken();

    // Non impostare Content-Type per FormData - il browser lo fa automaticamente con il boundary
    const isFormData = options.body instanceof FormData;

    const headers = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${apiConfig.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Only hard-redirect on 401 if the initial auth flow has completed.
        // During initial load, MSAL may not have the token ready yet - don't
        // cause a full page reload which resets React state and creates a loop.
        if (initialAuthDone.current) {
          clearDevAuth();
          clearToken();
          setUser(null);
          setLoading(false);
        }
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API Error: ${response.status}`);
    }

    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      return null;
    }
    const contentType = response.headers.get("content-type");
    return contentType?.includes("application/json")
      ? response.json()
      : response.text();
  };

  // Helper per verificare accesso a una sezione del portale
  const canAccessSection = useCallback(
    (sectionId) => {
      if (!user) return false;
      if (user.ruolo === 100000000) return true; // Admin bypass
      return user.allowedSections?.includes(sectionId) ?? false;
    },
    [user],
  );

  // Helper per verificare permesso su un'azione specifica
  const canPerformAction = useCallback(
    (actionId) => {
      if (!user) return false;
      if (user.ruolo === 100000000) return true; // Admin bypass
      return user.allowedActions?.includes(actionId) ?? false;
    },
    [user],
  );

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    getAccessToken,
    apiRequest,
    canAccessSection,
    canPerformAction,
    isDevMode: isDevAuthEnabled(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
// Build: 1769779705
